'use client';

import { useState, useEffect, useCallback, type FC, useContext } from 'react';
import type { EnqueuedQuestionnairesDialogProps } from './EnqueuedQuestionnairesDialog.types';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import {
  getEnqueuedQuestionnaires,
  type EnqueuedQuestionnaire,
} from '@/_api/admin-firms';
import { Dialog, Flex, Text, Loading } from '@/_components';
import { EnqueuedQuestionnaireItem } from './components/EnqueuedQuestionnaireItem';

export const EnqueuedQuestionnairesDialog: FC<
  EnqueuedQuestionnairesDialogProps
> = ({ isOpen, onClose }) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [enqueuedQuestionnaires, setEnqueuedQuestionnaires] = useState<
    Array<EnqueuedQuestionnaire>
  >([]);

  const loadEnqueuedQuestionnaires = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);

      const response = await getEnqueuedQuestionnaires({ token });

      setEnqueuedQuestionnaires(response?.enqueued_questionnaires || []);
    } catch (error) {
      addToast('error', 'Failed to load queued questionnaires', { error });
    } finally {
      setLoading(false);
    }
  }, [token, addToast]);

  useEffect(() => {
    if (isOpen) {
      loadEnqueuedQuestionnaires();
    } else {
      setEnqueuedQuestionnaires([]);
      setLoading(false);
    }
  }, [isOpen, loadEnqueuedQuestionnaires]);

  const hasQuestionnaires: boolean =
    !isLoading && !!enqueuedQuestionnaires?.length;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Queued Questionnaires">
      <Dialog.Content>
        {isLoading ? (
          <Flex isYCentered isXCentered gap={4}>
            <Loading size={24} />
            <Text type="paragraph">Loading queued questionnaires...</Text>
          </Flex>
        ) : hasQuestionnaires ? (
          <Flex
            isColumn={true}
            gap={8}
            style={{
              maxHeight: '60vh',
              overflowY: 'auto',
            }}
          >
            {enqueuedQuestionnaires.map((questionnaire, index) => (
              <EnqueuedQuestionnaireItem
                key={index}
                questionnaire={questionnaire}
              />
            ))}
          </Flex>
        ) : (
          <Text type="paragraph">No questionnaires are currently queued.</Text>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
